import { useState } from 'react';
import { STATE_RULES } from '@/util/constants';
import { useUpdateEncountersPreferencesProvider } from '@/api/update-preferred-provider';
import { useEncounter } from '@/contexts/encounter-context';
import { NewPatientModal } from './new-patient-modal';
import { VideoModal } from './video-modal';
import { GetProviderResponseItem } from '@/api/api.schemas';
import { usePostSubmitNavigation } from '@/hooks/use-post-submit-navigation';

interface SelectProviderButtonContainerProps {
  provider?: GetProviderResponseItem;
  children: (onClick: () => void, loading: boolean) => React.ReactNode;
}

export function SelectProviderButtonContainer({
  provider,
  children,
}: SelectProviderButtonContainerProps) {
  const { encounter, invalidateEncounter } = useEncounter();
  const { navigatePostSubmit } = usePostSubmitNavigation();

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);

  const { mutate: updatePreferredProvider, isPending } = useUpdateEncountersPreferencesProvider();

  function onSelect(preferredProviderId: string | null, hasSeenProvider: boolean) {
    return updatePreferredProvider(
      {
        encounterId: encounter.id,
        data: { preferredProviderId, hasSeenProvider },
      },
      {
        onSuccess: async (data) => {
          if (!data.videoRequired) {
            invalidateEncounter();
            navigatePostSubmit();
            return;
          }

          setIsVideoModalOpen(true);
        },
      },
    );
  }

  const handleSelect = () => {
    if (provider === undefined) {
      onSelect(null, false);
      return;
    }

    if (
      encounter?.stateRules
        .map((rule) => rule.ruleId)
        .includes(STATE_RULES.faceToFaceRequiredToTreatAndPrescribeOnce) &&
      provider.askNewPatientQuestion
    ) {
      setIsNewPatientModalOpen(true);
      return;
    }

    onSelect(provider.id, false);
  };

  return (
    <>
      {children(handleSelect, isPending)}

      {/* If the provider is null, this modal is not going to be open
          so its save to render them only if there is a provider */}
      {provider && (
        <NewPatientModal
          isOpen={isNewPatientModalOpen}
          onClose={() => setIsNewPatientModalOpen(false)}
          provider={provider}
          onSelect={onSelect}
        />
      )}

      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => {
          invalidateEncounter();
          setIsVideoModalOpen(false);
        }}
      />
    </>
  );
}
